import React, { useEffect, useState } from 'react';
import CareTeamPopover from './CareTeamPopover';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import axios from 'axios';
import {
    Box,
    Button,
    Divider,
    FormGroup,
    Grid,
    Link,
    Modal,
    Paper,
    Popover,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { HedisMetricStatusEnum } from '../../../Enum/HedisMetricStatusEnum';
import { HedisMetricsVisit } from '../../../Models/HedisMetrics/HedisMetricsVisit.model';
import { ClaimSourceEnum } from '../../../Enum/ClaimSourceEnum';
import Avatar from '@mui/material/Avatar';
import { RootState } from '../../../reducers';
import { modalStyle, apiString } from '../../../utils/constants';
import phoneFormat from '../../../utils/phoneFormat';
import { formatUtcDate } from '../../../utils/timeFormat';
import { noteFormatter } from '../../HIE/Modals/TeamNotificationModal';
import MemberHedisTiles from '../MemberHedisTiles/MemberHedisTiles';
import MemberPreviousChartsModal from '../MemberPreviousChartsModal/MemberPreviousChartsModal';
import AssignCareTeamModal from './modals/AssignCareTeamModal';
import SelectAlternateProviderDialog from '../../CareTeams/SelectAlternateProviderDialog';
import { setShowPreviousCharts } from '../../../store/memberDemographicsDetails.slice';
import Authorization from '../../../utils/Authorization';
import { fetchRoles } from '../../../store/shared.slice';
import { fetchTeamDetail } from '../../../store/careTeams.slice';
import { fetchMemberDemoGraphicInfo } from '../../../store/memberDetail.slice';
import { fetchMemberLifecycle } from '../../../store/memberLifecycle.slice';
import MemberLifecycleBar from '../MemberLifecycleBar';

const prepareHedisStatus = (metric) => {
    if (metric?.IsExpired || metric?.MetricStatus === HedisMetricStatusEnum.Expired) {
        return `Expired`;
    } else if (metric?.MetricStatus === HedisMetricStatusEnum.Completed) {
        return `Completed`;
    } else if (metric?.MetricStatus === HedisMetricStatusEnum.CompletedAndControlled) {
        return `Completed And Controlled`;
    } else if (metric?.MetricStatus === HedisMetricStatusEnum.CompletedAndNotControlled) {
        return `Completed And Not Controlled`;
    }
    return '';
};

export const fontColor = (metric) => {
    if (metric?.IsApplicable) {
        if (metric?.IsExpired || metric?.MetricStatus === HedisMetricStatusEnum.Expired) {
            return 'error.main';
        } else if (metric?.MetricStatus === HedisMetricStatusEnum.Completed || metric?.MetricStatus === HedisMetricStatusEnum.CompletedAndControlled) {
            return 'success.main';
        } else if (metric?.MetricStatus === HedisMetricStatusEnum.CompletedAndNotControlled) {
            return 'warning.main';
        } else {
            return 'rgba(0,0,0,0.8)';
        }
    } else {
        return 'rgba(0,0,0,0.4)';
    }
};

const TeamProviders: React.FC<{ memberId: string; primaryProviderReference?: any }> = ({ memberId, primaryProviderReference }) => {
    const dispatch = useAppDispatch();
    const [providerOptions, setProviderOptions] = useState<any[]>([]);

    useEffect(() => {
        dispatch(fetchRoles());
        axios.get(`${apiString}/Provider/getactiveproviders`).then((response) => {
            if (response.status === 200) {
                const responseOptions = response.data.map((provider: any) => ({
                    ProviderId: provider.Id,
                    ProviderName: `${provider.LastName}, ${provider.FirstName}`,
                    RoleName: provider.Role.RoleName,
                    RoleId: provider.Role.Id,
                }));
                setProviderOptions(responseOptions);
            }
        });
    }, [dispatch]);
    const { roles, allProviders, isLoading } = useSelector((state: RootState) => state.shared);
    const { selectedTeam } = useSelector((state: RootState) => state.careTeams);
    const selectedMember = useSelector((state: RootState) => state.memberDetail.selectedMember);
    const [showAssignCareTeamModal, setShowAssignCareTeamModal] = useState(false);
    const [showSelectAlternateDialog, setShowSelectAlternateDialog] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleAlternateProviderRefresh = () => {
        if (memberId && selectedTeam?.Id) {
            const p1 = dispatch(fetchMemberDemoGraphicInfo(memberId));
            const p2 = dispatch(fetchTeamDetail(selectedTeam.Id));
            Promise.all([p1, p2])
                .then(() => {
                    setShowSelectAlternateDialog(false);
                })
                .catch((err) => {
                    console.error('Error in refresh:', err);
                });
        }
    };

    const teamRoles = selectedTeam?.TeamRoles || [];
    // Only alternates with an active provider replace the team's provider; inactive ones would show as "Unassigned".
    const teamRoleAlternates = (selectedTeam?.TeamRoleAlternates || []).filter(
        (alt) => providerOptions.length === 0 || providerOptions.some((p) => p.ProviderId === alt.AssignedProviderId)
    );
    const roleNameById = roles?.reduce((acc, role) => {
        acc[role.Id] = role.Name;
        return acc;
    }, {} as Record<string, string>);
    const auth = new Authorization();
    const canManageCareTeams = auth.isInPermission('Manage teams');

    const isProviderAlternate = (roleId: string, providerId: string): boolean => {
        return teamRoleAlternates.some((alt: any) => alt.MemberId === memberId && alt.RoleId === roleId && alt.AssignedProviderId === providerId);
    };

    const isProviderBothPrimaryAndAlternate = (roleId: string, providerId: string): boolean => {
        const isPrimary = primaryProviderReference?.TeamRoleId === roleId;
        const isAlternate = isProviderAlternate(roleId, providerId);
        return isPrimary && isAlternate;
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handlePopoverClose = () => {
        setAnchorEl(null);
    };
    const popoverOpen = Boolean(anchorEl);

    return (
        <Box sx={{ p: 0, mb: 0, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 0 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, m: 0, lineHeight: 1.2 }}>
                    {selectedTeam?.Name ? `Care Team - ${selectedTeam.Name}` : 'No Care Team Assigned'}
                </Typography>
                {canManageCareTeams && (
                    <IconButton aria-label="care team actions" onClick={handleMenuOpen} sx={{ m: 0 }} size="small">
                        <MoreVertIcon />
                    </IconButton>
                )}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            if (selectedTeam?.Id) {
                                window.open(`/provider/careteams/${selectedTeam.Id}`, '_blank');
                            }
                        }}
                    >
                        Manage Team
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            setShowAssignCareTeamModal(true);
                        }}
                    >
                        Assign Team
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            setShowSelectAlternateDialog(true);
                        }}
                    >
                        Select Alternate Providers
                    </MenuItem>
                </Menu>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', minHeight: 36, mb: 1 }}>
                {teamRoles.length > 0 &&
                    roleNameById &&
                    Object.keys(roleNameById).length > 0 &&
                    (() => {
                        const alternatesByRole = new Map<string, any>();
                        const memberAlternates = teamRoleAlternates.filter((alt: any) => alt.MemberId === memberId);

                        memberAlternates.forEach((alt: any) => {
                            alternatesByRole.set(alt.RoleId, alt);
                        });

                        const providersToDisplay = teamRoles.map((tr: any) => {
                            const alternate = alternatesByRole.get(tr.RoleId);
                            if (alternate) {
                                return {
                                    RoleId: alternate.RoleId,
                                    AssignedProviderId: alternate.AssignedProviderId,
                                    ProviderName: alternate.ProviderName,
                                };
                            }
                            return tr;
                        });

                        return providersToDisplay.map((tr: any) => {
                            let roleName = roleNameById?.[tr.RoleId];
                            if (!roleName || roleName.trim() === '') {
                                roleName = 'Provider';
                            }
                            let initials = '';
                            if (/^[A-Z]{2,4}$/.test(roleName.replace(/\s/g, ''))) {
                                initials = roleName.replace(/\s/g, '').slice(0, 4);
                            } else {
                                initials = roleName
                                    .split(' ')
                                    .map((word: string) => word[0])
                                    .join('')
                                    .toUpperCase();
                            }
                            const provider = allProviders?.find((p: any) => p.Id === tr.AssignedProviderId || p.ProviderId === tr.AssignedProviderId);
                            const providerName =
                                provider?.ProviderName ||
                                provider?.FullName ||
                                `${provider?.FirstName || ''} ${provider?.LastName || ''}`.trim() ||
                                tr.ProviderName ||
                                'Unassigned';
                            const isPrimary = primaryProviderReference?.TeamRoleId === tr.RoleId;
                            const isAlternate = isProviderAlternate(tr.RoleId, tr.AssignedProviderId);

                            let bgColor = 'primary.main';
                            let boxShadow: any = 1;
                            if (isPrimary) {
                                bgColor = '#9C27B0';
                                boxShadow = '0 0 8px 2px rgba(156, 39, 176, 0.6)';
                            } else if (isAlternate) {
                                bgColor = 'success.main';
                                boxShadow = '0 0 8px 2px rgba(76, 175, 80, 0.6)';
                            }

                            return (
                                <Tooltip key={tr.RoleId + '_' + tr.AssignedProviderId} title={providerName} placement="top">
                                    <Avatar
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            bgcolor: bgColor,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            mr: 0.5,
                                            boxShadow: boxShadow,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            transition: 'all 0.2s ease',
                                        }}
                                        onClick={handlePopoverOpen}
                                    >
                                        {initials}
                                    </Avatar>
                                </Tooltip>
                            );
                        });
                    })()}
            </Box>
            <CareTeamPopover
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                teamRoles={teamRoles}
                roleNameById={roleNameById}
                allProviders={allProviders}
                isLoading={isLoading}
                avatarSize={40}
                primaryRoleId={primaryProviderReference?.TeamRoleId}
                memberId={memberId}
                teamRoleAlternates={teamRoleAlternates}
            />
            <AssignCareTeamModal open={showAssignCareTeamModal} onClose={() => setShowAssignCareTeamModal(false)} memberId={memberId} />
            {selectedTeam && selectedMember && (
                <SelectAlternateProviderDialog
                    open={showSelectAlternateDialog}
                    onClose={() => setShowSelectAlternateDialog(false)}
                    member={selectedMember}
                    roles={roles}
                    providerOptions={providerOptions}
                    selectedTeam={selectedTeam}
                    onRefresh={handleAlternateProviderRefresh}
                />
            )}
        </Box>
    );
};

const MemberInformation = (props) => {
    const { memberId } = props;
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { chartDetails, previousMemberCharts } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const { memberDefaultDiagnosis } = useSelector((state: RootState) => state.encounter);
    const { memberHealthPlans } = useSelector((state: RootState) => state.memberDetail);
    const { memberLifecycle } = useSelector((state: RootState) => state.memberLifecycle);
    const [diagnosisAnchorEl, setDiagnosisAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [showHedisDetail, setShowHedisDetail] = useState(false);
    const [hedisDetail, setHedisDetail] = useState<HedisMetricsVisit>(null);
    const [hedisType, setHedisType] = useState<string>('');
    useEffect(() => {
        if (memberId) {
            dispatch(fetchMemberLifecycle(memberId));
            // First fetch the team by member ID to get the team ID, then fetch full team detail with alternates
            const fetchTeamWithAlternates = async () => {
                try {
                    const response = await axios.get(`${apiString}/CareTeams/bymemberid?memberId=${memberId}`);
                    if (response.data?.Id) {
                        // Now fetch the full team detail with alternates
                        const teamDetailResponse = await axios.get<any>(`${apiString}/careTeams/${response.data.Id}`);
                        // Dispatch after we confirm the data
                        dispatch(fetchTeamDetail(response.data.Id));
                    }
                } catch (error) {
                    console.error('Error fetching team by member ID:', error);
                }
            };
            fetchTeamWithAlternates();
        }
    }, [dispatch, memberId]);
    const handleViewPreviousCharts = () => {
        dispatch(setShowPreviousCharts(true));
    };
    const handleClickDiagnosis = (event: React.MouseEvent<HTMLButtonElement>) => {
        setDiagnosisAnchorEl(event.currentTarget);
    };

    const handleCloseDiagnosis = () => {
        setDiagnosisAnchorEl(null);
    };

    const diagnosisOpen = Boolean(diagnosisAnchorEl);
    const diagnosisId = diagnosisOpen ? 'simple-popover' : undefined;

    return (
        <>
            <MemberPreviousChartsModal />
            <Box sx={{ backgroundColor: '#ddd', p: 1, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <FormGroup sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    <Grid container spacing={1}>
                        <Paper square elevation={3} sx={{ p: 2, display: 'flex', width: '100%', flexDirection: isMobile ? 'column' : 'row' }}>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <Stack spacing={1}>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Last Seen:</Typography>
                                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                            {chartDetails?.LastCHPVisit
                                                ? (() => {
                                                      const d = toDate(chartDetails?.LastCHPVisit);
                                                      return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                                  })()
                                                : '-'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Risk Score:</Typography>
                                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                            {chartDetails?.RiskScore}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Contracting Plan:</Typography>
                                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                            {chartDetails?.ContractingHealthPlan?.Name}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Insurance:</Typography>
                                        <Grid container columnGap={2} justifyContent={'flex-end'}>
                                            {memberHealthPlans.map((plan, index) => {
                                                return (
                                                    <Grid display={'flex'} key={index}>
                                                        <Typography textAlign={'right'} fontStyle={'italic'} variant="body2" marginRight={1}>
                                                            {plan.Priority ? plan.Priority : '-'}
                                                        </Typography>
                                                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                                            {plan?.Name ? plan?.Name : '-'}
                                                        </Typography>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {!isMobile && <Divider orientation="vertical" flexItem sx={{ marginX: 2 }} />}

                            <Grid size={{ xs: 12, md: 3 }}>
                                <Stack spacing={1}>
                                    <TeamProviders memberId={memberId} primaryProviderReference={chartDetails?.PrimaryProviderReference} />
                                </Stack>
                            </Grid>

                            {!isMobile && <Divider orientation="vertical" flexItem sx={{ marginX: 2 }} />}

                            <Grid size={{ xs: 12, md: 4 }}>
                                <Stack spacing={1}>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Address:</Typography>
                                        <Stack justifyContent={'flex-end'} spacing={0}>
                                            <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                                {`${chartDetails?.MainAddress?.Address1 ? chartDetails?.MainAddress?.Address1 : '-'}${
                                                    chartDetails?.MainAddress?.Address2 ? ', ' + chartDetails?.MainAddress?.Address2 : ''
                                                } `}
                                            </Typography>
                                            <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                                {`${chartDetails?.MainAddress?.City ? chartDetails?.MainAddress?.City : '-'}${
                                                    chartDetails?.MainAddress?.State ? ', ' + chartDetails?.MainAddress?.State : ''
                                                } ${chartDetails?.MainAddress?.ZipCode ? chartDetails?.MainAddress?.ZipCode : ''}`}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    {chartDetails.IsDifferMeetingAddress && (
                                        <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                            <Typography variant="body2">Meeting Address:</Typography>
                                            <Stack justifyContent={'flex-end'} spacing={0}>
                                                <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                                    {`${chartDetails?.MainMeetingAddress?.Address1 ? chartDetails?.MainMeetingAddress?.Address1 : '-'}${
                                                        chartDetails?.MainMeetingAddress?.Address2 ? ', ' + chartDetails?.MainMeetingAddress?.Address2 : ''
                                                    } `}
                                                </Typography>
                                                <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                                    {`${chartDetails?.MainMeetingAddress?.City ? chartDetails?.MainMeetingAddress?.City : '-'}${
                                                        chartDetails?.MainMeetingAddress?.State ? ', ' + chartDetails?.MainMeetingAddress?.State : ''
                                                    } ${chartDetails?.MainMeetingAddress?.ZipCode ? chartDetails?.MainMeetingAddress?.ZipCode : ''}`}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )}
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Phones:</Typography>
                                        <Grid container columnGap={2} justifyContent={'flex-end'}>
                                            {chartDetails?.Phones?.map((phone, index) => {
                                                return (
                                                    <Grid key={index} display={'flex'} color={phone.IsPrimary ? 'primary.main' : 'rgba(0,0,0,0.5)'}>
                                                        <Typography textAlign={'right'} variant="body2" marginRight={1}>
                                                            {phone.Textable ? 'Text -' : 'No Text -'}
                                                        </Typography>
                                                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                                            {phone.Number ? phoneFormat(phone.Number) : '-'}
                                                        </Typography>
                                                        {phone.IsPrimary && (
                                                            <Tooltip title="Primary Phone">
                                                                <StarIcon color="primary" sx={{ fontSize: '1rem', marginLeft: 1 }} />
                                                            </Tooltip>
                                                        )}
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} borderBottom={1}>
                                        <Typography variant="body2">Primary Language:</Typography>
                                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                            {chartDetails?.PrimaryLanguage?.Name}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {!isMobile && <Divider orientation="vertical" flexItem sx={{ marginX: 2 }} />}

                            <Grid size={{ xs: 12, md: 2 }} mt={isMobile ? 2 : 0}>
                                <Stack spacing={2}>
                                    <Button
                                        aria-describedby={diagnosisId}
                                        variant="outlined"
                                        onClick={handleClickDiagnosis}
                                        sx={{ width: '100%' }}
                                        size={isSmallScreen ? 'small' : 'medium'}
                                    >
                                        View Primary Diagnosis
                                    </Button>
                                    <Popover
                                        id={diagnosisId}
                                        open={diagnosisOpen}
                                        anchorEl={diagnosisAnchorEl}
                                        onClose={handleCloseDiagnosis}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        PaperProps={{ style: { maxWidth: isSmallScreen ? '300px' : '400px' } }}
                                    >
                                        <Box sx={{ p: 2 }}>
                                            <Typography variant="body1" fontWeight={700}>
                                                Primary Diagnosis
                                            </Typography>
                                            <Typography variant="body2">
                                                {Boolean(memberDefaultDiagnosis?.DefaultDiagnosis?.FourthDiagnosis)
                                                    ? memberDefaultDiagnosis?.DefaultDiagnosis?.FourthDiagnosis?.Code +
                                                      ' - ' +
                                                      memberDefaultDiagnosis?.DefaultDiagnosis?.FourthDiagnosis?.Name
                                                    : Boolean(memberDefaultDiagnosis?.DefaultDiagnosis?.ThirdDiagnosis)
                                                    ? memberDefaultDiagnosis?.DefaultDiagnosis?.ThirdDiagnosis?.Code +
                                                      ' - ' +
                                                      memberDefaultDiagnosis?.DefaultDiagnosis?.ThirdDiagnosis?.Name
                                                    : Boolean(memberDefaultDiagnosis?.DefaultDiagnosis?.SecondDiagnosis)
                                                    ? memberDefaultDiagnosis?.DefaultDiagnosis?.SecondDiagnosis?.Code +
                                                      ' - ' +
                                                      memberDefaultDiagnosis?.DefaultDiagnosis?.SecondDiagnosis?.Name
                                                    : Boolean(memberDefaultDiagnosis?.DefaultDiagnosis?.FirstDiagnosis)
                                                    ? memberDefaultDiagnosis?.DefaultDiagnosis?.FirstDiagnosis?.Code +
                                                      ' - ' +
                                                      memberDefaultDiagnosis?.DefaultDiagnosis?.FirstDiagnosis?.Name
                                                    : Boolean(memberDefaultDiagnosis?.DefaultDiagnosis?.DiagnosisPointer)
                                                    ? memberDefaultDiagnosis?.DefaultDiagnosis?.DiagnosisPointer?.Code +
                                                      ' - ' +
                                                      memberDefaultDiagnosis?.DefaultDiagnosis?.DiagnosisPointer?.Name
                                                    : '-'}
                                            </Typography>
                                        </Box>
                                    </Popover>
                                    <Button
                                        disabled={previousMemberCharts.length > 0 ? false : true}
                                        aria-describedby={diagnosisId}
                                        variant="outlined"
                                        onClick={handleViewPreviousCharts}
                                        sx={{ width: '100%' }}
                                        size={isSmallScreen ? 'small' : 'medium'}
                                    >
                                        View Previous Charts ({previousMemberCharts.length})
                                    </Button>
                                </Stack>
                            </Grid>
                        </Paper>
                    </Grid>

                    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                        <MemberLifecycleBar memberId={memberId} memberLifecycle={memberLifecycle} />
                    </Box>

                    <MemberHedisTiles setHedisDetail={setHedisDetail} setHedisType={setHedisType} setShowHedisDetail={setShowHedisDetail} />

                    <Grid size={12}>
                        <Stack
                            direction={isSmallScreen ? 'column' : 'row'}
                            spacing={2}
                            sx={{ backgroundColor: 'white' }}
                            justifyContent={'center'}
                            alignItems={'center'}
                        >
                            <Typography variant="body2" fontWeight={'bold'} color={'error.main'}>
                                Expired
                            </Typography>
                            <Typography variant="body2" fontWeight={'bold'} color={'success.main'}>
                                Applicable And Controlled
                            </Typography>
                            <Typography variant="body2" fontWeight={'bold'} color={'warning.main'}>
                                Applicable And Not Controlled
                            </Typography>
                        </Stack>
                    </Grid>
                </FormGroup>
            </Box>
            <Modal
                open={showHedisDetail}
                onClose={() => {
                    setShowHedisDetail(false);
                }}
            >
                <Box sx={{ ...modalStyle, width: isSmallScreen ? '90%' : modalStyle.width }} style={{ paddingTop: '20px', paddingBottom: '20px' }}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            {hedisType}
                            <Typography variant="body1" sx={{ color: fontColor(hedisDetail), fontSize: 14 }}>
                                {prepareHedisStatus(hedisDetail)}
                            </Typography>
                        </Typography>
                        <Divider />
                    </Stack>
                    <Stack spacing={2} mt={2}>
                        <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                            <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                CPT Code :
                            </Typography>
                            <Typography>{Boolean(hedisDetail?.CPTcode) ? hedisDetail?.CPTcode : '-'}</Typography>
                        </Stack>
                        <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                            <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                {hedisDetail?.DateLastMet && !hedisDetail.IsMetAtThisVisit ? 'Last Not Met : ' : 'Last Met : '}
                            </Typography>
                            <Typography>{formatUtcDate(hedisDetail?.DateLastMet)}</Typography>
                        </Stack>
                        <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                            <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                Claim Number :
                            </Typography>
                            <Typography>{Boolean(hedisDetail?.ClaimNumber) ? hedisDetail?.ClaimNumber : '-'}</Typography>
                        </Stack>
                        <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                            <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                Claim Source :
                            </Typography>
                            <Typography>
                                {Boolean(hedisDetail?.ClaimSourceName)
                                    ? hedisDetail.ClaimSource === ClaimSourceEnum.ChpClaims && hedisDetail.ProviderName
                                        ? `${hedisDetail.ClaimSourceName} (${hedisDetail.ProviderName})`
                                        : hedisDetail.ClaimSourceName
                                    : '-'}
                            </Typography>
                        </Stack>
                        <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                            <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                Notes :
                            </Typography>
                            <Typography flex={1}>{Boolean(hedisDetail?.Note) ? hedisDetail?.Note : '-'}</Typography>
                        </Stack>
                        {Boolean(hedisDetail?.HedisAlert) && (
                            <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                                <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                    Lab Alert :
                                </Typography>
                                <div>{noteFormatter(hedisDetail?.HedisAlert)}</div>
                            </Stack>
                        )}
                        {hedisDetail?.DocumentUri && (
                            <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                                <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                    Document :
                                </Typography>
                                <Link target="_blank" underline="none" href={hedisDetail?.DocumentUri} sx={{ mt: '2px !important' }}>
                                    {hedisDetail?.DocumentName}
                                </Link>
                            </Stack>
                        )}
                        {hedisDetail?.ClaimSource === ClaimSourceEnum.HealthPlan && (
                            <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                                <Typography fontWeight={700} sx={{ ml: 1, mb: 1.5 }}>
                                    Updated Based On Health Plan :
                                </Typography>
                                <Typography>
                                    {Boolean(hedisDetail?.DateUpdatedByHealthPlan)
                                        ? (() => {
                                              const d = toDate(hedisDetail?.DateUpdatedByHealthPlan);
                                              return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                          })()
                                        : '-'}
                                </Typography>
                            </Stack>
                        )}
                        <Divider />
                        <Box>
                            <Stack direction="row" spacing={1} style={{ float: 'right' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setShowHedisDetail(false);
                                        setHedisDetail(null);
                                    }}
                                >
                                    Ok
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};
export default MemberInformation;
